import React, { useEffect, useState } from "react";
import {
  Loading,
  Row,
  Column,
  FormGroup,
  TextArea,
  Button,
  Tag,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from "carbon-components-react";
import { Download24, ArrowLeft24 } from "@carbon/icons-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Card from "../../components/Card/Card";
import http from "../../http-common";
import { getDocumentTypeDescription } from "../../services/Utils";
import useAuth from "../../hooks/useAuth";

const ViewPrototypePage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { isJury } = useAuth();
  const [prototype, setPrototype] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchPrototype();
  }, [uuid]);

  const fetchPrototype = async () => {
    try {
      const [protoResponse, docsResponse] = await Promise.all([
        http.get(`/prototypes/${uuid}`),
        http.get(`/prototypes/${uuid}/documents`),
      ]);
      setPrototype(protoResponse.data);
      setDocuments(docsResponse.data);
    } catch (error) {
      toast.error("Erro ao carregar protótipo");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      setDownloadingId(doc.id);
      const response = await http.get(`/prototypes/documents/download/${doc.id}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao descarregar documento");
    } finally {
      setDownloadingId(null);
    }
  };

  if (!isLoading && !prototype) {
    return (
      <Card title="Protótipo">
        <p>Protótipo não encontrado.</p>
      </Card>
    );
  }

  return (
    <Card title={`Protótipo — ${prototype?.companyName || ""}`}>
      <Loading
        active={isLoading}
        description="Active loading indicator"
        withOverlay={true}
      />

      {prototype && (
        <>
          <div style={{ marginBottom: "1rem" }}>
            <Tag type={prototype.prototypeStatus === "SUBMITTED" ? "green" : "gray"}>
              {prototype.prototypeStatus === "SUBMITTED" ? "Submetido" : "Rascunho"}
            </Tag>
          </div>

          <FormGroup legendText="ACESSO À PLATAFORMA" className="section-header">
            <fieldset className="bx--fieldset">
              <Row className="mb-4">
                <Column sm={12} md={12} lg={12}>
                  <p className="bx--label">
                    Link de Acesso:{" "}
                    <a
                      href={
                        prototype.platformUrl &&
                        /^https?:\/\//i.test(prototype.platformUrl)
                          ? prototype.platformUrl
                          : `https://${prototype.platformUrl}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: "break-all" }}
                    >
                      {prototype.platformUrl}
                    </a>
                  </p>
                </Column>
              </Row>

              <Row className="mb-2">
                <Column sm={12} md={12} lg={12}>
                  <p className="bx--label" style={{ marginBottom: "0.5rem" }}>
                    Credenciais de Acesso ({prototype.credentials?.length || 0}/10)
                  </p>
                  {prototype.credentials && prototype.credentials.length > 0 ? (
                    <DataTable
                      rows={prototype.credentials.map((cred, index) => ({
                        id: String(cred.id || index),
                      }))}
                      headers={[
                        { key: "description", header: "Descrição" },
                        { key: "credentialUsername", header: "Utilizador" },
                        { key: "credentialPassword", header: "Senha" },
                      ]}
                    >
                      {({ headers, getHeaderProps, getTableProps }) => (
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map((header) => (
                                <TableHeader
                                  {...getHeaderProps({ header })}
                                  key={header.key}
                                >
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {prototype.credentials.map((cred, index) => (
                              <TableRow key={cred.id || index}>
                                <TableCell>{cred.description}</TableCell>
                                <TableCell>{cred.credentialUsername}</TableCell>
                                <TableCell>{cred.credentialPassword}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </DataTable>
                  ) : (
                    <p style={{ color: "#6f6f6f", fontStyle: "italic" }}>
                      Nenhuma credencial disponível.
                    </p>
                  )}
                </Column>
              </Row>
            </fieldset>
          </FormGroup>

          <FormGroup legendText="DESCRIÇÃO E INFORMAÇÃO" className="section-header">
            <fieldset className="bx--fieldset">
              <Row className="mb-4">
                <Column sm={12} md={12} lg={12}>
                  <TextArea
                    id="functionalityDescription"
                    labelText="Principais Funcionalidades"
                    value={prototype.functionalityDescription || ""}
                    rows={5}
                    disabled
                  />
                </Column>
              </Row>

              {prototype.additionalInfo && (
                <Row>
                  <Column sm={12} md={12} lg={12}>
                    <TextArea
                      id="additionalInfo"
                      labelText="Informações Adicionais"
                      value={prototype.additionalInfo}
                      rows={4}
                      disabled
                    />
                  </Column>
                </Row>
              )}
            </fieldset>
          </FormGroup>

          <FormGroup legendText="DOCUMENTOS" className="section-header">
            <fieldset className="bx--fieldset">
              {documents.length === 0 ? (
                <p>Nenhum documento disponível.</p>
              ) : (
                <DataTable
                  rows={documents.map((doc) => ({ ...doc, id: String(doc.id) }))}
                  headers={[
                    { key: "fileName", header: "Nome do Ficheiro" },
                    { key: "documentType", header: "Tipo de Documento" },
                    { key: "actions", header: "Descarregar" },
                  ]}
                >
                  {({ rows, headers, getHeaderProps, getTableProps }) => (
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map((header) => (
                            <TableHeader {...getHeaderProps({ header })} key={header.key}>
                              {header.header}
                            </TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>{doc.fileName}</TableCell>
                            <TableCell>
                              {getDocumentTypeDescription(doc.documentType)}
                            </TableCell>
                            <TableCell>
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={Download24}
                                disabled={downloadingId === doc.id}
                                onClick={() => handleDownload(doc)}
                              >
                                {downloadingId === doc.id ? "A descarregar..." : "Descarregar"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </DataTable>
              )}
            </fieldset>
          </FormGroup>

          <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem" }}>
            <Button kind="ghost" renderIcon={ArrowLeft24} onClick={() => navigate(-1)}>
              Voltar
            </Button>
            {isJury && (
              <Link
                className="bx--btn bx--btn--primary"
                to={`../score-prototype/${uuid}`}
              >
                Pontuar
              </Link>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default ViewPrototypePage;
